if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        if (!this.wishlistProduct()) return;

        this.onWishlistChange = () => this.syncWishlistState();
        document.addEventListener(window.DimexWishlist.CHANGE_EVENT, this.onWishlistChange);
        this.syncWishlistState();
      }

      disconnectedCallback() {
        if (!this.onWishlistChange) return;
        document.removeEventListener(window.DimexWishlist.CHANGE_EVENT, this.onWishlistChange);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.wishlistProduct()) {
          if (this.isOnWishlist) {
            window.location.href = (window.routes && window.routes.wishlist_url) || '/pages/tuotelista';
            return;
          }
          this.handleWishlistAdd();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      /* Liquid only puts a Swym payload on the button when this buyer may not
         purchase this product, so the payload is what marks a wishlist button.
         A disabled button keeps its own sold-out or unavailable message instead
         of a wishlist state. */
      wishlistProduct() {
        if (!window.DimexWishlist || !window.DimexWishlist.isGuest()) return null;
        if (!this.submitButton || this.submitButton.disabled) return null;
        if (!this.submitButton.dataset.swymProduct) return null;

        try {
          return JSON.parse(this.submitButton.dataset.swymProduct);
        } catch (e) {
          console.error('[Wishlist] invalid product data on submit button:', e);
          return null;
        }
      }

      syncWishlistState() {
        const product = this.wishlistProduct();
        if (!product) return;
        // A product added a moment ago is showing its confirmation message; the
        // timer that clears it settles the final state.
        if (this.wishlistFeedbackTimer) return;

        window.DimexWishlist.isWishlisted(product, (onWishlist) => this.setWishlistState(onWishlist));
      }

      setWishlistState(onWishlist) {
        this.isOnWishlist = onWishlist;
        this.submitButton.classList.toggle('button--on-wishlist', onWishlist);
        this.submitButtonText.textContent = onWishlist
          ? window.variantStrings.onWishlist
          : window.variantStrings.addToWishlist;

        // The button navigates to the wishlist in this state, which the label
        // alone does not say.
        if (onWishlist) {
          this.submitButton.setAttribute('title', window.variantStrings.viewWishlist);
        } else {
          this.submitButton.removeAttribute('title');
        }
      }

      handleWishlistAdd() {
        const product = this.wishlistProduct();
        if (!product) return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        window.DimexWishlist.addToWishlist(
          product,
          () => {
            this.submitButtonText.textContent = window.variantStrings.addedToWishlist || this.submitButtonText.textContent;
            this.finishWishlistSubmit(true);
          },
          (error) => {
            console.error('[Wishlist] addToWishlist failed:', error);
            this.handleErrorMessage(window.variantStrings.wishlistError, true);
            this.finishWishlistSubmit(false);
          }
        );
      }

      // The confirmation message is temporary; the button then settles into the
      // state the wishlist is actually in.
      finishWishlistSubmit(added) {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');

        clearTimeout(this.wishlistFeedbackTimer);
        this.wishlistFeedbackTimer = setTimeout(() => {
          this.wishlistFeedbackTimer = null;
          this.setWishlistState(added || this.isOnWishlist === true);
        }, 1800);
      }

      handleErrorMessage(errorMessage = false, useButtonFallback = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (this.errorMessageWrapper) {
          this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

          this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

          if (errorMessage) {
            this.errorMessage.textContent = errorMessage;
          }
          return;
        }

        // Card and quick-add forms have no wrapper; the wishlist button is the
        // only place a guest can see that the save failed.
        if (!this.submitButton) return;
        if (useButtonFallback && errorMessage && this.submitButtonText) {
          this.submitButtonText.textContent = errorMessage;
          this.submitButton.setAttribute('title', errorMessage);
          return;
        }
        if (!errorMessage) {
          this.submitButton.removeAttribute('title');
        }
      }

      // Variant switches route through here and rewrite the label, so the
      // wishlist state has to be taken down or put back with it.
      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          this.submitButton.classList.remove('button--on-wishlist');
          this.submitButton.removeAttribute('title');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.wishlistProduct()
            ? window.variantStrings.addToWishlist
            : window.variantStrings.addToCart;
          this.syncWishlistState();
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
